import { useEffect, useState } from 'react';
import {
	agendasCiselnikyQuery,
	type AgendyAisOvmDto,
	ovmAgendasQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { type FormDefinition2, useFormBuilder } from '@gov-nx/core/service';
import type {
	CommonMultiselectProps,
	CommonSelectProps,
	FormInputProps,
	FormRadioProps,
} from '@gov-nx/ui/types';
import {
	type AutocompleteFormData,
	getMultiselectDefinition,
	getRadioDefinition,
	getSelectDefinition,
	toDateReadable,
	today,
	useAutocompleteDefinition,
	useDateDefinition,
	yearsFromNow,
} from '@gov-nx/utils/common';
import {
	selectedDateOfValidityConsentToReadable,
	selectedToReadable,
} from '../detail/utils';
import { usePpdfSouhlasyLocale } from '../usePpdfSouhlasyLocale';
import { useOvmDataQuery } from './useOvmDataQuery';

export interface PpdfSouhlasyUdeleniStep2 {
	fromOvmId: string;
	toOvmId: AutocompleteFormData<AgendyAisOvmDto>;
	fromAgendaId: string;
	toAgendaId: (string | null)[];
	sharedAgendaMultiselect: (string | null)[];
	consentValidityInterval: 'URCITY' | 'NEURCITY';
	dateOfValidityConsent: Date | null;
}

interface Fields {
	fromOvmId: CommonSelectProps;
	toOvmId: CommonSelectProps;
	fromAgendaId: CommonSelectProps;
	toAgendaId: CommonMultiselectProps;
	sharedAgendaMultiselect: CommonMultiselectProps;
	dateOfValidityConsent: FormInputProps;
	consentValidityInterval: FormRadioProps;
}

export type PpdfSouhlasyUdeleniFormStep2 = FormDefinition2<
	PpdfSouhlasyUdeleniStep2,
	Fields
>;

export interface AgendaDataSelectedLabels {
	selectedFromOvm?: string;
	selectedToOvm?: string;
	selectedFromAgenda?: string;
	selectedToAgenda?: string;
	selectedDateOfValidityConsent?: string;
	selectedSharedAgendaData?: string;
}
const CONSENT_VALIDITY_YEARS_AHEAD = 1;

export const useFormStep2 = ({ isDisabled }: { isDisabled: boolean }) => {
	const { t } = usePpdfSouhlasyLocale();
	const [prevFromOvmId, setPrevFromOvmId] = useState<string>();
	const [prevToOvmId, setPrevToOvmId] = useState<string>();

	const { isLoading, ovmOptions: fromOvmOptions } = useOvmDataQuery({
		maAgendy: true,
	});
	const [prevFromAgendaId, setPrevFromAgendaId] = useState<string>();

	const toOvmId = useAutocompleteDefinition({
		name: 'toOvmId',
		label: t('formular.prijemce-udaju.label'),
		message: t('formular.prijemce-udaju.message'),
		placeholder: t('formular.prijemce-udaju.default-label'),
		requiredMessage: t('formular.prijemce-udaju.validace.povinny'),
		isDisabled,
	});

	const toAgendaId = getMultiselectDefinition({
		name: 'toAgendaId',
		label: t('formular.agenda-prijemce.label'),
		message: t('formular.agenda-prijemce.message'),
		placeholder: t('formular.agenda-prijemce.placeholder'),
		isDisabled: isDisabled,
		options: [],
	});

	const fromOvmId = getSelectDefinition({
		name: 'fromOvmId',
		label: t('formular.spravce-udaju.label'),
		message: t('formular.spravce-udaju.message'),
		isDisabled,
		isRequired: true,
		isRequiredMessage: t('formular.spravce-udaju.validace.povinny'),
		options: [
			{ label: t('formular.spravce-udaju.default-label'), value: '' },
			...fromOvmOptions,
		],
	});

	const fromAgendaId = getSelectDefinition({
		name: 'fromAgendaId',
		label: t('formular.agenda-spravce.label'),
		message: t('formular.agenda-spravce.message'),
		isDisabled: isDisabled,
		isRequired: true,
		isRequiredMessage: t('formular.agenda-spravce.validace.povinny'),
		options: [{ label: t('formular.agenda-spravce.default-label'), value: '' }],
	});
	const min = today(undefined, true);
	const minErrorMessage = t('formular.souhlas-do-data.validace.min', {
		datum: toDateReadable(min),
	});

	const sharedAgendaMultiselect = getMultiselectDefinition({
		name: 'sharedAgendaMultiselect',
		label: t('formular.sdilene-udaje.label'),
		message: t('formular.sdilene-udaje.message'),
		placeholder: t('formular.sdilene-udaje.placeholder'),
		options: [],
		isRequired: true,
		isRequiredMessage: t('formular.sdilene-udaje.validace.povinny'),
		isDisabled: isDisabled,
	});

	const consentValidityInterval = getRadioDefinition({
		name: 'consentValidityInterval',
		label: t('formular.interval.label'),
		options: [
			{
				label: t('formular.interval.neurcity.label'),
				name: 'NEURCITY',
			},
			{
				label: t('formular.interval.urcity.label'),
				name: 'URCITY',
			},
		],
		defaultValue: 'NEURCITY',
		isDisabled,
	});

	const dateOfValidityConsent = useDateDefinition({
		name: 'dateOfValidityConsent',
		label: t('formular.souhlas-do-data.label'),
		min: { min, errorMessage: minErrorMessage },
		isRequiredWhen: {
			fieldName: consentValidityInterval.field.field.name,
			is: 'URCITY',
			errorMessage: t('formular.souhlas-do-data.validace.povinny'),
		},
		defaultValue: yearsFromNow(CONSENT_VALIDITY_YEARS_AHEAD),
		isDisabled: isDisabled,
	});

	const form = useFormBuilder<PpdfSouhlasyUdeleniStep2, Fields>([
		fromOvmId,
		toOvmId,
		fromAgendaId,
		toAgendaId,
		sharedAgendaMultiselect,
		consentValidityInterval,
		dateOfValidityConsent,
	]);

	const updatedForm = useFormStep2Selects(form, isDisabled);

	const {
		fromAgendaId: fromAgendaIdValue,
		fromOvmId: fromOvmIdValue,
		toOvmId: toOvmIdValue,
	} = form.formMethods.watch();

	// cleanup fromAgendaId
	useEffect(() => {
		if (
			(prevFromOvmId && prevFromOvmId !== fromOvmIdValue) ||
			(prevFromOvmId && fromOvmIdValue === undefined)
		) {
			form.formMethods.setValue('fromAgendaId', '', { shouldValidate: false });
		}

		setPrevFromOvmId(fromOvmIdValue);
	}, [fromOvmIdValue, prevFromOvmId, form.formMethods]);

	// cleanup toAgendaId
	useEffect(() => {
		if (
			(prevToOvmId && prevToOvmId !== toOvmIdValue?.selected?.kod) ||
			(prevToOvmId && toOvmIdValue === undefined)
		) {
			form.formMethods.setValue('toAgendaId', [], { shouldValidate: false });
		}

		setPrevToOvmId(toOvmIdValue?.selected?.kod);
	}, [toOvmIdValue, prevToOvmId, form.formMethods]);

	// cleanup sharedAgendaData
	useEffect(() => {
		if (
			(prevFromAgendaId && prevFromAgendaId !== fromAgendaIdValue) ||
			(prevFromAgendaId && fromAgendaIdValue === undefined)
		) {
			form.formMethods.setValue('sharedAgendaMultiselect', [], {
				shouldValidate: false,
			});
		}

		setPrevFromAgendaId(fromAgendaIdValue);
	}, [fromAgendaIdValue, prevFromAgendaId, form.formMethods]);

	return {
		isLoading: isLoading,
		form: updatedForm.form,
		selectedLabels: updatedForm.selectedLabels,
		noValuesForSharedAgendaData: updatedForm.noValuesForSharedAgendaData,
	};
};

export const useFormStep2Selects = <
	Values extends PpdfSouhlasyUdeleniStep2,
	Fields2 extends Fields,
>(
	form: FormDefinition2<Values, Fields2>,
	isDisabled: boolean
): {
	noValuesForSharedAgendaData: boolean;
	selectedLabels: AgendaDataSelectedLabels;
	form: FormDefinition2<Values, Fields2>;
} => {
	const { t } = usePpdfSouhlasyLocale();
	const { axiosInstance } = useAxiosInstance();

	const { ovmOptions: fromOvmOptions } = useOvmDataQuery({ maAgendy: true });
	const { ovmOptions: toOvmOptions } = useOvmDataQuery({ maAgendy: false });
	const {
		fromOvmId,
		toOvmId,
		fromAgendaId,
		toAgendaId,
		sharedAgendaMultiselect,
		consentValidityInterval,
		dateOfValidityConsent,
	} = form.formMethods.watch();

	const queryFromAgendaMaUdaje = true;
	const queryFromAgenda = usePoQuery({
		queryKey: ['agenda', fromOvmId, queryFromAgendaMaUdaje],
		queryFn: () => ovmAgendasQuery(axiosInstance, fromOvmId, true),
		enabled: !!fromOvmId,
	});

	const fromAgendaOptions =
		queryFromAgenda.data?.seznam?.map(({ kod, nazev }) => ({
			value: kod ?? '',
			label: nazev ?? '',
		})) ?? [];

	const queryToAgendaMaUdaje = false;
	// The recipient needs the agendas the OVM performs / operates in,
	// not the ones it announces – hence pusobnost=true.
	const queryToAgendaPusobnost = true;
	const queryToAgenda = usePoQuery({
		queryKey: ['agenda', toOvmId, queryToAgendaMaUdaje, queryToAgendaPusobnost],
		queryFn: () =>
			ovmAgendasQuery(
				axiosInstance,
				toOvmId?.selected?.kod ?? '',
				queryToAgendaMaUdaje,
				queryToAgendaPusobnost
			),
		enabled: !!toOvmId && !!toOvmId?.selected?.kod,
	});

	const toAgendaOptions =
		queryToAgenda.data?.seznam?.map(({ kod, nazev }) => ({
			value: kod ?? '',
			label: nazev ?? '',
		})) ?? [];

	const querySharedAgendaData = usePoQuery({
		queryKey: ['agenda-ciselniky', fromOvmId, fromAgendaId],
		queryFn: async () =>
			agendasCiselnikyQuery(
				axiosInstance,
				fromAgendaOptions.find((agenda) => agenda.value === fromAgendaId)
					?.value ?? ''
			),

		enabled: !!fromAgendaId && !!fromOvmId && !!fromAgendaOptions.length,
	});

	const sharedAgendaDataOptions =
		querySharedAgendaData.data?.seznam?.map(({ kod, nazevCs }) => ({
			value: kod ?? '',
			label: nazevCs ?? '',
		})) ?? [];

	const noValuesForSharedAgendaData =
		!!fromAgendaId &&
		!querySharedAgendaData.isLoading &&
		!sharedAgendaDataOptions.length;

	return {
		noValuesForSharedAgendaData,
		selectedLabels: {
			selectedFromOvm: fromOvmOptions.find((ovm) => ovm.value === fromOvmId)
				?.label,
			selectedFromAgenda: fromAgendaOptions.find(
				(agenda) => agenda.value === fromAgendaId
			)?.label,
			selectedToOvm: toOvmOptions.find(
				(ovm) => ovm.value === toOvmId?.selected?.kod
			)?.label,
			selectedToAgenda: selectedToReadable(
				toAgendaId,
				toAgendaOptions,
				t('rekapitulace.detaily.agenda-prijemce-nevybrano')
			),
			selectedDateOfValidityConsent: selectedDateOfValidityConsentToReadable({
				consentValidityInterval,
				dateOfValidityConsent,
				indefiniteText: t('rekapitulace.detaily.souhlas-neurcity'),
				emptyText: t('rekapitulace.detaily.nevybrano'),
			}),
			selectedSharedAgendaData: selectedToReadable(
				sharedAgendaMultiselect,
				sharedAgendaDataOptions,
				t('rekapitulace.detaily.sdilene-udaje-nevybrano')
			),
		},
		form: {
			...form,
			fields: {
				...form.fields,
				sharedAgendaMultiselect: {
					...form.fields.sharedAgendaMultiselect,
					field: {
						...form.fields.sharedAgendaMultiselect.field,
						options: sharedAgendaDataOptions,
						isDisabled: isDisabled || sharedAgendaDataOptions.length === 0,
						disabled: isDisabled || sharedAgendaDataOptions.length === 0,
					},
				},

				fromAgendaId: {
					...form.fields.fromAgendaId,
					field: {
						...form.fields.fromAgendaId.field,
						options: [
							{ label: t('formular.agenda-spravce.default-label'), value: '' },
							...fromAgendaOptions,
						],
						isDisabled: isDisabled || fromAgendaOptions.length === 0,
						disabled: isDisabled || fromAgendaOptions.length === 0,
					},
				},
				toAgendaId: {
					...form.fields.toAgendaId,
					field: {
						...form.fields.toAgendaId.field,
						options: toAgendaOptions,
						isDisabled: isDisabled || toAgendaOptions.length === 0,
						disabled: isDisabled || toAgendaOptions.length === 0,
					},
				},
			},
		},
	};
};
